import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import api from "@/lib/api";
import {
  Download,
  Tv,
  ExternalLink,
  BookOpen,
  Check,
  Info,
  BarChart3,
  User,
  Globe2,
} from "lucide-react";
import CountryBubbleMap from "@/components/CountryBubbleMap";

/**
 * /downloads — public page listing the Mirrorloops Signage Player install
 * guides for each supported display platform. Buttons trigger a real PDF
 * download; the manifest fetch fills in per-platform version/size/date.
 */

const PLATFORM_META = {
  lg: {
    label: "LG",
    accent: "#E8D6A3",
    tagline: "webOS · 55NU870BPLA and friends",
    bullets: [
      "webOS 4.5+ (webOS 6 recommended)",
      "Built-in browser as kiosk shell",
      "Auto-launch + full-screen boot",
    ],
  },
  sony: {
    label: "Sony",
    accent: "#00E5FF",
    tagline: "Android TV / Google TV · Bravia X75L, X80L, A80L",
    bullets: [
      "Google TV or Android TV 9+",
      "Chrome kiosk mode",
      "Ambient / screensaver disabled",
    ],
  },
};

function bytesLabel(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Downloads() {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [params] = useSearchParams();
  const wantsAdmin = params.get("admin") === "1";

  useEffect(() => {
    (async () => {
      try {
        const [r, meRes] = await Promise.all([
          api.get("/downloads/manifest"),
          api.get("/auth/me").catch(() => ({ data: null })),
        ]);
        setManifest(r.data);
        setMe(meRes.data);
        if (wantsAdmin && meRes.data && ["hq_admin", "manager"].includes(meRes.data.role)) {
          try {
            const s = await api.get("/downloads/stats?days=30");
            setStats(s.data);
          } catch {
            /* silent — non-admins already filtered */
          }
        }
      } catch (e) {
        setError("Couldn't load the downloads list.");
      }
    })();
  }, [wantsAdmin]);

  const backend = process.env.REACT_APP_BACKEND_URL;

  const startDownload = (item) => {
    setDownloading(item.id);
    // Direct anchor click — respects Content-Disposition inline + lets the
    // browser prompt "Save as" naturally.
    const url = `${backend}/api${item.download_url.replace(/^\/api/, "")}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = item.filename;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => setDownloading(null), 1200);
  };

  return (
    <PublicShell>
      <div data-testid="downloads-page" className="max-w-5xl mx-auto px-6 pt-32 pb-24">
        {/* Hero */}
        <div className="mb-12">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-4">
            <Download size={12} /> Downloads · Signage Player
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
            Get the Mirrorloops player
            <br />
            <span className="text-[#D4B878]">on your screens.</span>
          </h1>
          <p className="mt-6 text-white/60 text-base max-w-2xl leading-relaxed">
            Pick your display's platform below. Each pack is a step-by-step
            install guide that walks a venue installer through pairing,
            browser boot, and kiosk mode in under 10 minutes. Signed native
            apps (LG IPK, Sony APK) ship in a follow-up release — the
            guide-based flow works today on every device we recommend.
          </p>
        </div>

        {/* Personalisation notice for logged-in owners */}
        {me && (
          <div
            data-testid="downloads-personalised-notice"
            className="mb-6 border border-[#CCFF00]/25 bg-[#CCFF00]/[0.04] rounded-lg p-3 flex items-center gap-2 text-[12px]"
          >
            <User size={14} className="text-[#CCFF00]" />
            <span className="text-white/85">
              Signed in as{" "}
              <span className="font-semibold text-white">{me.name || me.email}</span>{" "}
              — the download will include a personalised{" "}
              <span className="text-[#CCFF00]">Step 0 QR</span> for
              pairing your next screen.
            </span>
          </div>
        )}

        {/* Downloads grid */}
        {error && (
          <div className="text-red-400 text-sm py-6" data-testid="downloads-error">
            {error}
          </div>
        )}
        {!error && !manifest && (
          <div className="text-white/40 text-sm py-6">Loading manifest…</div>
        )}
        {manifest && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="downloads-grid"
          >
            {manifest.items.map((item) => {
              const meta = PLATFORM_META[item.id] || {
                label: item.platform,
                accent: "#D4B878",
                tagline: "",
                bullets: [],
              };
              return (
                <div
                  key={item.id}
                  data-testid={`downloads-card-${item.id}`}
                  className="border border-white/10 rounded-lg bg-white/[0.02] p-6 flex flex-col"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div
                        className="text-[10px] uppercase tracking-[0.24em] font-semibold mb-2"
                        style={{ color: meta.accent }}
                      >
                        For {meta.label}
                      </div>
                      <div className="text-xl font-semibold text-white">
                        {item.platform}
                      </div>
                      <div className="text-xs text-white/50 mt-1">
                        {meta.tagline}
                      </div>
                    </div>
                    <Tv size={22} style={{ color: meta.accent }} />
                  </div>

                  <ul className="space-y-1.5 mb-5 flex-1">
                    {meta.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2 text-[13px] text-white/70"
                      >
                        <Check
                          size={12}
                          className="mt-1 shrink-0"
                          style={{ color: meta.accent }}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center gap-2 text-[10px] text-white/40 font-mono mb-3">
                    v{item.version} · {bytesLabel(item.bytes)} · updated{" "}
                    {item.updated_on}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.device_examples.map((d) => (
                      <span
                        key={d}
                        className="px-2 py-0.5 bg-white/[0.04] border border-white/10 rounded-full text-[10px] uppercase tracking-[0.14em] text-white/60 font-mono"
                      >
                        {d}
                      </span>
                    ))}
                  </div>

                  <button
                    onClick={() => startDownload(item)}
                    data-testid={`downloads-button-${item.id}`}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full text-black font-semibold text-sm transition-all hover:brightness-110"
                    style={{ backgroundColor: meta.accent }}
                  >
                    {downloading === item.id ? (
                      <>
                        <Check size={14} /> Starting download…
                      </>
                    ) : (
                      <>
                        <Download size={14} /> Download for {meta.label}
                      </>
                    )}
                  </button>

                  <div className="mt-3 flex items-center justify-between text-[10px] text-white/40">
                    <span className="flex items-center gap-1">
                      <BookOpen size={10} /> PDF install guide
                    </span>
                    <a
                      href={`${backend}/api${item.download_url.replace(/^\/api/, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`downloads-preview-${item.id}`}
                      className="flex items-center gap-1 hover:text-[#D4B878]"
                    >
                      Preview <ExternalLink size={10} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Disclosure */}
        <div className="mt-10 border border-white/10 rounded-lg bg-white/[0.02] p-4 flex gap-3">
          <Info size={16} className="text-[#D4B878] shrink-0 mt-0.5" />
          <div className="text-[13px] text-white/60 leading-relaxed">
            <span className="text-white font-semibold">
              About these downloads.
            </span>{" "}
            Each pack is a step-by-step PDF install guide, not a signed native
            binary. On LG the player runs in the built-in webOS browser; on
            Sony it runs in Chrome via Google TV — both give you a Chromium
            runtime with kiosk mode, and both pair to your Mirrorloops account
            using a 6-character code. Signed IPK + APK builds are on the
            roadmap.
          </div>
        </div>

        {/* Admin stats — hidden unless ?admin=1 AND user is hq_admin/manager */}
        {stats && (
          <div
            data-testid="downloads-stats"
            className="mt-8 border border-white/10 rounded-lg bg-white/[0.02] p-5"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-4">
              <BarChart3 size={12} /> Download analytics · last {stats.window_days} days
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <div className="p-3 bg-black/40 border border-white/10 rounded">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Total hits</div>
                <div
                  data-testid="stats-total"
                  className="text-3xl font-semibold text-white mt-1 font-mono"
                >
                  {stats.total}
                </div>
              </div>
              {Object.entries(stats.per_platform || {}).map(([k, v]) => {
                const accent = k === "lg" ? "#E8D6A3" : k === "sony" ? "#00E5FF" : "#D4B878";
                const personalRate = v.total ? Math.round((v.personalized / v.total) * 100) : 0;
                return (
                  <div
                    key={k}
                    data-testid={`stats-platform-${k}`}
                    className="p-3 bg-black/40 border border-white/10 rounded"
                  >
                    <div
                      className="text-[10px] uppercase tracking-[0.18em] font-semibold"
                      style={{ color: accent }}
                    >
                      {k}
                    </div>
                    <div className="text-3xl font-semibold text-white mt-1 font-mono">
                      {v.total}
                    </div>
                    <div className="text-[10px] text-white/50 mt-1 font-mono">
                      {v.personalized} personalised ({personalRate}%) · {v.unique_users || 0} users
                    </div>
                  </div>
                );
              })}
            </div>
            {stats.daily?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">
                  Daily hits
                </div>
                <div className="flex items-end gap-1 h-24" data-testid="stats-daily-chart">
                  {stats.daily.map((d) => {
                    const total = (d.lg || 0) + (d.sony || 0);
                    const max = Math.max(
                      ...stats.daily.map((x) => (x.lg || 0) + (x.sony || 0)),
                      1,
                    );
                    const h = Math.round((total / max) * 100);
                    return (
                      <div
                        key={d.day}
                        title={`${d.day}: ${total}`}
                        className="flex-1 flex flex-col-reverse gap-0.5 min-w-[8px]"
                      >
                        {d.lg > 0 && (
                          <div
                            style={{
                              height: `${(d.lg / max) * 100}%`,
                              backgroundColor: "#E8D6A3",
                            }}
                            className="w-full rounded-t-sm"
                          />
                        )}
                        {d.sony > 0 && (
                          <div
                            style={{
                              height: `${(d.sony / max) * 100}%`,
                              backgroundColor: "#00E5FF",
                            }}
                            className="w-full"
                          />
                        )}
                        {total === 0 && (
                          <div className="w-full h-[2px] bg-white/10" />
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex gap-4 text-[9px] uppercase tracking-[0.18em] text-white/40">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: "#E8D6A3" }} />
                    LG
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: "#00E5FF" }} />
                    Sony
                  </span>
                </div>
              </div>
            )}

            {/* Country bubble map — where in the world the guide is being
                pulled from, split LG vs Sony. */}
            {stats.per_country?.length > 0 && (
              <div className="mt-6" data-testid="stats-country-map">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2 flex items-center gap-1.5">
                  <Globe2 size={11} /> Country split · LG vs Sony
                </div>
                <CountryBubbleMap data={stats.per_country} />
              </div>
            )}
          </div>
        )}

        {/* Related */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-[11px] text-white/40">
          <div>
            Questions? Read the{" "}
            <Link
              to="/concierge?q=signage-player-compat"
              className="text-[#D4B878] hover:text-[#E8D6A3] underline decoration-dotted underline-offset-2"
            >
              player compatibility FAQ
            </Link>
            .
          </div>
          <div className="flex gap-4">
            <Link to="/venues" className="hover:text-white">
              For Venue Owners →
            </Link>
            <Link to="/register" className="hover:text-[#D4B878]">
              Start free →
            </Link>
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
